import { useEffect, useRef, useState } from "react";
import socket from "../lib/socket";

function getToken() {
  try {
    const fromStorage =
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("ga_token") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("jwt") ||
      "";

    if (fromStorage) return fromStorage;

    const qs = new URLSearchParams(window.location.search);
    return qs.get("token") || qs.get("auth") || "";
  } catch {
    return "";
  }
}

const MESSAGE_TTL_MS = 5 * 60 * 1000;
const MAX_MESSAGES = 50;

export default function useChannelChat(room, { canPost = true, role = "" } = {}) {
  const [status, setStatus] = useState("connecting");
  const [messages, setMessages] = useState([]);

  const seenRef = useRef(new Set());
  const pendingRef = useRef(null);
  const activeRoomRef = useRef("");

  useEffect(() => {
    if (!socket || !room) return;

    const nextRoom = String(room || "").trim();
    const prevRoom = String(activeRoomRef.current || "").trim();
    activeRoomRef.current = nextRoom;

    setStatus("connecting");
    setMessages([]);
    seenRef.current = new Set();

    socket.auth = { token: getToken() };

    const leaveRoom = (targetRoom) => {
      const leaveTarget = String(targetRoom || "").trim();
      if (!leaveTarget) return;
      socket.emit("leave", { room: leaveTarget, role });
    };

    const joinRoom = () => {
      if (prevRoom && prevRoom !== nextRoom) {
        leaveRoom(prevRoom);
      }
      const token = getToken();
      socket.auth = { token };
      socket.emit("join", { room: nextRoom, role, token });
    };

    const flushPending = () => {
      if (!pendingRef.current) return;
      const text = pendingRef.current;
      pendingRef.current = null;
      socket.emit("chat:send", { room: nextRoom, msg: text, token: getToken() });
    };

    const pruneMessages = () => {
      const cutoff = Date.now() - MESSAGE_TTL_MS;
      const now = Date.now();
      setMessages((prev) =>
        prev
          .filter((m) => Number(m?.ts || 0) >= cutoff)
          .filter((m) => {
            const expiresAt = Number(m?.expiresAt || 0);
            return !expiresAt || expiresAt > now;
          })
          .slice(-MAX_MESSAGES)
      );
    };

    const onConnect = () => {
      setStatus("live");
      joinRoom();
      flushPending();
    };

    const onDisconnect = () => setStatus("idle");

    const onHistory = (history) => {
      const items = Array.isArray(history) ? history : [];
      const cutoff = Date.now() - MESSAGE_TTL_MS;
      const now = Date.now();
      const filtered = items
        .filter((m) => String(m?.room || nextRoom) === nextRoom)
        .filter((m) => Number(m?.ts || 0) >= cutoff)
        .filter((m) => {
          const expiresAt = Number(m?.expiresAt || 0);
          return !expiresAt || expiresAt > now;
        })
        .slice(-MAX_MESSAGES)
        .map((m) => ({ ...m, text: typeof m?.text === "string" ? m.text : String(m?.msg ?? "") }));

      seenRef.current = new Set(
        filtered.map((msg) => `${msg?.room || nextRoom}:${msg?.id || ""}:${msg?.ts || ""}:${msg?.text || ""}`)
      );
      setMessages(filtered);
    };

    const onMessage = (m) => {
      if (!m) return;

      const msgRoom = String(m?.room || "").trim() || nextRoom;
      if (msgRoom !== nextRoom) return;

      const safeText = typeof m?.text === "string" ? m.text : String(m?.msg ?? "");
      const msg = { ...m, room: msgRoom, text: safeText };
      const key = `${msgRoom}:${msg?.id || ""}:${msg?.ts || ""}:${msg?.text || ""}`;

      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);

      const cutoff = Date.now() - MESSAGE_TTL_MS;
      const now = Date.now();
      const expiresAt = Number(msg?.expiresAt || 0);
      if (expiresAt && expiresAt <= now) return;

      setMessages((prev) =>
        [...prev, msg]
          .filter((item) => Number(item?.ts || 0) >= cutoff)
          .filter((item) => {
            const itemExpiresAt = Number(item?.expiresAt || 0);
            return !itemExpiresAt || itemExpiresAt > now;
          })
          .slice(-MAX_MESSAGES)
      );
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("chat:history", onHistory);
    socket.on("chat:message", onMessage);

    const pruneId = window.setInterval(pruneMessages, 10000);

    if (!socket.connected) {
      socket.connect();
    } else {
      onConnect();
    }

    return () => {
      window.clearInterval(pruneId);
      if (activeRoomRef.current === nextRoom) {
        leaveRoom(nextRoom);
        activeRoomRef.current = "";
      }
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("chat:history", onHistory);
      socket.off("chat:message", onMessage);
      setMessages([]);
      seenRef.current = new Set();
    };
  }, [room, role]);

  function send(text) {
    const trimmed = String(text || "").trim();
    if (!trimmed || !canPost) return;

    if (!socket.connected) {
      pendingRef.current = trimmed;
      setStatus("connecting");
      socket.auth = { token: getToken() };
      socket.connect();
      return;
    }

    socket.emit("chat:send", { room, msg: trimmed, token: getToken() });
  }

  return { status, messages, send };
}
